import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { Button, ListGroup, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useDispatch, useSelector } from "react-redux";
import { permissionsInterface } from "../onBoarding/onBoardingTypes";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { IFloorInfo, ITowerInfo } from "../Projects/CreateProject/ManageTower";
import routePaths from "../../Navigation/routePaths";

interface IPlotData {
    id: number;
    coordinates: number[][];
    color: string;
    title: string;
    facing_direction?: string;
    area?: string;
    status?: string;
};

const TowerStatusView = () => {
    const navigate = useNavigate();
    const { tower_id, project_id } = useParams();
    const [plotData, setPlotData] = useState<IPlotData[]>([]);
    const [fullPageLoder, setFullPageLoder] = useState(false);
    const [towerImage, setTowerImage] = useState<string>("");
    const token = useSelector((state: any) => state.OnBoardingReducer);
    const [permissions, setPermissions] = useState<permissionsInterface | null>(null);
    const [towerInfo, setTowerInfo] = useState<any>(null);
    const [showFloorInfo, setShowFloorInfo] = useState<boolean>(false);
    const [floorInfo, setFloorInfo] = useState<IFloorInfo | null>(null);

    useEffect(() => {
        if (token && token.token && token.permissions && !!token.permissions.length) {
            setPermissions(token.permissions.find((item: any) => item.name === "PROJECTS"));
        }
    }, [token]);

    useEffect(() => {
        if (permissions && permissions.can_read && tower_id && project_id) {
            getMarkingData(tower_id, project_id);
        }
    }, [permissions]);

    const getMarkingData = (towerId: string, projectId: string) => {
        setFullPageLoder(true);
        WebService.getAPI({ action: `get/tower?project_uuid=${projectId}&tower_uuid=${towerId}` })
            .then((res: any) => {
                setFullPageLoder(false);
                setTowerInfo(res.tower_info);
                setPlotData(res.tower_info?.coordinates_data);
                setTowerImage(res.tower_info?.tower_image);
            })
            .catch((e: any) => { setFullPageLoder(false); });
    };

    const getFloorName = (plot: any) => {
        if (towerInfo && towerInfo.floor_info && towerInfo.floor_info.length) {
            const floor = towerInfo.floor_info.find((floor: any) => floor.id === plot.floor_id);
            if (floor) {
                return floor.floor_name;
            }
        }
        return `Floor`;
    };

    const onClickFloor = (plot: any) => {
        const floor = towerInfo && towerInfo.floor_info && towerInfo.floor_info.find((floor: any) => floor.id === plot.floor_id);
        setFloorInfo(floor);
        setShowFloorInfo(true);
    }

    const getUnitCountsByStatus = (floorInfo: IFloorInfo) => {
        const units = towerInfo?.tower_units;
        const floor_units: any[] = floorInfo?.units.map((unit: any) => {
            const fullUnit = units.find((u: any) => u.id == unit);
            if (fullUnit) {
                return fullUnit;
            }
        });

        const soldCount = floor_units.filter(unit => unit.status === 'SOLD').length;

        return <>
            <ListGroup.Item as="li" className="border-0 border-bottom">Total Units: {floor_units.length}</ListGroup.Item>
            <ListGroup.Item as="li" className="border-0 border-bottom">Available Units: {floor_units.length - soldCount}</ListGroup.Item>
            <ListGroup.Item as="li" className="border-0 border-bottom">Sold Units: {soldCount}</ListGroup.Item>
        </>;
    };

    return (
        <div className="app-page">
            <div className="d-flex justify-content-between align-items-center "> <PageTitle title="Floor Status" backArrow={true} /> </div>

            {permissions && permissions.can_read && towerInfo &&
                <div className="d-flex gap-2 mb-3">
                    <span className="badge bg-info text-white px-3 py-2 font-20">Total Units: {towerInfo.total_units}</span>
                    <span className="badge bg-success text-white px-3 py-2 font-20">Available: {towerInfo.available_units}</span>
                    <span className="badge bg-danger text-white px-3 py-2 font-20">Sold: {towerInfo.sold_units}</span>
                </div>
            }

            {permissions && permissions.can_read && <div style={{ height: "800px", width: "1250px", overflow: "auto" }}>
                <TransformWrapper>
                    <TransformComponent>
                        <svg style={{ height: "600px", width: "1250px", backgroundImage: `url(${towerImage})`, backgroundRepeat: "no-repeat", padding: "0px" }}>
                            {plotData && !!plotData.length && plotData.map((plot: any) => {
                                const points = plot.coordinates.map(([x, y]: any) => `${x},${y}`).join(" ");
                                return (
                                    <g style={{ cursor: "pointer" }} key={plot.id} onClick={() => onClickFloor(plot)}>
                                        <polygon points={points}
                                            fill={"rgba(117, 190, 218, 0.2)"}
                                            stroke={''}
                                            strokeWidth="2"
                                            style={{ transition: "fill 150ms ease" }}
                                            onMouseEnter={(e) => {
                                                (e.currentTarget as SVGPolygonElement).setAttribute("fill", "rgba(117, 190, 218, 0.5)");
                                            }}
                                            onMouseLeave={(e) => {
                                                (e.currentTarget as SVGPolygonElement).setAttribute("fill", "rgba(117, 190, 218, 0.2)");
                                            }} />
                                        {/* Calculate text width and polygon bounding box */}
                                        {(() => {
                                            // Calculate polygon centroid for better center placement
                                            const getPolygonCentroid = (coords: [number, number][]) => {
                                                let x = 0, y = 0, area = 0;
                                                const n = coords.length;
                                                for (let i = 0; i < n; i++) {
                                                    const [x0, y0] = coords[i];
                                                    const [x1, y1] = coords[(i + 1) % n];
                                                    const a = x0 * y1 - x1 * y0;
                                                    area += a;
                                                    x += (x0 + x1) * a;
                                                    y += (y0 + y1) * a;
                                                }
                                                area *= 0.5;
                                                if (area === 0) return [coords[0][0], coords[0][1]];
                                                x /= (6 * area);
                                                y /= (6 * area);
                                                return [x, y];
                                            };
                                            const [centroidX, centroidY] = getPolygonCentroid(plot.coordinates);

                                            // Estimate text width (simple approximation: 8px per character)
                                            const textWidth = plot.title ? plot.title.length * 8 : 40;
                                            // Find bounding box of polygon
                                            const xs = plot.coordinates.map(([x]: any) => x);
                                            const ys = plot.coordinates.map(([, y]: any) => y);
                                            const minX = Math.min(...xs);
                                            const maxX = Math.max(...xs);
                                            const minY = Math.min(...ys);
                                            const maxY = Math.max(...ys);
                                            const boxWidth = maxX - minX;
                                            const boxHeight = maxY - minY;
                                            // Set background width: min(boxWidth, textWidth + padding)
                                            const bgWidth = Math.min(boxWidth, textWidth + 16);
                                            const bgHeight = Math.min(boxHeight, 28);

                                            return (
                                                <>
                                                    <rect x={centroidX - bgWidth / 2} y={centroidY - bgHeight / 2} width={bgWidth} height={bgHeight} fill="rgba(117, 190, 218, 0.4)" rx={6} />
                                                    <text x={centroidX} y={centroidY} fill="#fff" fontSize="14" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                                        {getFloorName(plot)}
                                                    </text>
                                                </>
                                            );
                                        })()}
                                    </g>
                                );
                            })}
                        </svg>
                    </TransformComponent>
                </TransformWrapper>
            </div>}

            <Modal show={showFloorInfo} onHide={() => { setShowFloorInfo(false); setFloorInfo(null); }}
                aria-labelledby="contained-modal-title-vcenter"
                centered >
                <Modal.Header closeButton>
                    <Modal.Title>Floor Information</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {floorInfo &&
                        <>
                            <h6 className="text-center font-22 font-bold text-success">{floorInfo.floor_name}</h6>
                            <ListGroup as="ul" className="border-0">
                                {getUnitCountsByStatus(floorInfo)}
                            </ListGroup>
                            <div className="text-center">
                                <Button variant="info" className="mt-3 text-center" onClick={() => { navigate(routePaths.FLOOR_STATUS_VIEW.path + floorInfo.id + "/" + tower_id + "/" + project_id) }}>
                                    View Floor
                                </Button>
                            </div>
                        </>
                    }
                </Modal.Body>
            </Modal>

        </div>

    );
};

export default TowerStatusView;